// src/pages/Reports.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import {
  ClipboardIcon,
  BanknotesIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";

import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";

type CashAccount = {
  account: string;
  opening: number;
  in: number;
  out: number;
  closing: number;
};

type CashProjection = {
  period: string;
  receivables_due: number;
  payables_due: number;
  projected_cash: number;
};

type CashAnalysisResponse = {
  accounts: CashAccount[];
  projection: CashProjection[];
};

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

export default function Reports() {
  const [balance, setBalance] = useState<any>(null);
  const [income, setIncome] = useState<any>(null);
  const [cash, setCash] = useState<CashAccount[]>([]);
  const [projection, setProjection] = useState<CashProjection[]>([]);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const todayStr = useMemo(() => ymdTodayLocal(), []);
  const yearStartStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  }, []);

  useEffect(() => {
    if (!company_id) return;

    apiGet(`/reports/balance-sheet?date=${todayStr}`).then(setBalance);
    apiGet(`/reports/income-statement?start=${yearStartStr}&end=${todayStr}`).then(
      setIncome
    );

    apiGet<CashAnalysisResponse>(
      `/reports/cash-analysis?start_date=${yearStartStr}&end_date=${todayStr}`
    ).then((data) => {
      setCash(data?.accounts ?? []);
      setProjection(data?.projection ?? []);
    });
  }, [company_id, todayStr, yearStartStr]);

  const cashTotal = useMemo(() => {
    return Array.isArray(cash) ? cash.reduce((sum, c) => sum + (c.closing || 0), 0) : 0;
  }, [cash]);

  const totalAssets = balance?.Assets?.["Total Assets"];
  const netIncome = income?.net_income;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const reportCardCls =
    "flex flex-col items-center justify-center " +
    "min-h-[140px] sm:min-h-[180px] " +
    `${A.card} ` +
    "px-4 py-6 sm:px-6 sm:py-8 " +
    "hover:shadow-md hover:border-gray-300 " +
    "transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4`}>
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} text-center`}>{t("reports.title")}</h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      <div className="w-full max-w-3xl px-2 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 items-stretch w-full">
          {/* Balance Sheet */}
          <Link
            to={companyRoute(company_id, "/reports/balance-sheet")}
            className={reportCardCls}
          >
            <ClipboardIcon className="w-7 h-7 mb-2 text-appleblue" />
            <div className="text-[13px] font-medium text-gray-700 text-center leading-tight">
              {t("balanceSheet.title")}
            </div>
            <div className={`mt-3 ${A.title} ${A.num}`}>
              {money(typeof totalAssets === "number" ? totalAssets : null)}
            </div>
          </Link>

          {/* Income Statement */}
          <Link
            to={companyRoute(company_id, "/reports/income-statement")}
            className={reportCardCls}
          >
            <BanknotesIcon className="w-7 h-7 mb-2 text-appleblue" />
            <div className="text-[13px] font-medium text-gray-700 text-center leading-tight">
              {t("incomeStatement.title")}
            </div>
            <div className={`mt-3 ${A.title} ${A.num}`}>
              {money(typeof netIncome === "number" ? netIncome : null)}
            </div>
          </Link>

          {/* Cash Analysis */}
          <Link
            to={companyRoute(company_id, "/reports/cash-analysis")}
            className={reportCardCls}
          >
            <WalletIcon className="w-7 h-7 mb-2 text-appleblue" />
            <div className="text-[13px] font-medium text-gray-700 text-center leading-tight">
              {t("cashAnalysis.title")}
            </div>
            <div className={`mt-3 ${A.title} ${A.num}`}>
              {cash && cash.length > 0 ? money(cashTotal) : "—"}
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
